import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { motion, useAnimate } from "framer-motion";

type CardSpec = {
  id: 1 | 2 | 3 | 4;
  img: string;
  x: number;
  y: number;
  w: number;
  z: number;
};

export default function ServiceIntroCard3Sequence() {
  const SHADOW = "drop-shadow(0 18px 38px rgba(0,0,0,0.12))";
  const START_FILTER = `blur(12px) saturate(0.95) brightness(1.02) ${SHADOW}`;
  const END_FILTER = `blur(0px) saturate(1) brightness(1) ${SHADOW}`;
  const START_OPACITY = 0.28; // 0.2~0.35 취향

  const BASE_W = 623;
  const BASE_H = 604; //  이미지가 가로로 길어서 높이는 적당히 (필요하면 조정)
  const outerRef = useRef<HTMLDivElement | null>(null);
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [outerWidth, setOuterWidth] = useState(BASE_W);

  const [scope, animate] = useAnimate();
  const ranRef = useRef(false);

  const scale = Math.min(outerWidth / BASE_W, 1);
  const scaledH = BASE_H * scale;

  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width ?? BASE_W;
      setOuterWidth(w);
    });

    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  //  카드 배치 (여기 x/y/w만 미세 조정하세요)
  const cards: CardSpec[] = [
    {
      id: 1,
      img: "/assets/images/serviceIntro/card3_1.png",
      x: 40,
      y: 60,
      w: 278,
      z: 40,
    }, // 왼쪽 위
    {
      id: 2,
      img: "/assets/images/serviceIntro/card3_2.png",
      x: 340,
      y: 240,
      w: 205,
      z: 30,
    }, // 오른쪽
    {
      id: 3,
      img: "/assets/images/serviceIntro/card3_3.png",
      x: 160,
      y: 320,
      w: 162,
      z: 20,
    }, // 왼쪽 중간
    {
      id: 4,
      img: "/assets/images/serviceIntro/card3_4.png",
      x: 70,
      y: 385,
      w: 150,
      z: 10,
    }, // 왼쪽 아래
  ];

  const stroke = "rgba(17,24,39,0.20)";
  const strokeWidth = 2;
  const lineDefs = useMemo(() => {
    const p1 = { x: cards[0].x + cards[0].w - 6, y: cards[0].y + 76 };

    //  카드2 "윗면" 접점: 상단 중앙 (y는 top과 거의 동일하게)
    const p2 = {
      x: cards[1].x + cards[1].w * 0.5,
      y: cards[1].y + 0, //  윗면에 붙이려면 0~4 사이로
    };

    //  두 번 꺾임을 만드는 중간 좌표
    const xTurn = p1.x + 44; // 첫 수평 길이
    const yTurn = p2.y; // 카드2 상단 높이까지 수직 이동

    //  마지막을 반드시 (p2.x, p2.y)로 찍어 "윗면에 닿게" 함
    const d12 = `
    M ${p1.x} ${p1.y}
    L ${xTurn} ${p1.y}
    L ${xTurn} ${yTurn - 30}
    L ${p2.x - 20} ${yTurn - 30}
     L ${p2.x - 20} ${p2.y + 50}
  `;

    return { line12: { d: d12 } };
  }, [cards]);

  useEffect(() => {
    let alive = true;
    const el = hostRef.current;
    if (!el) return;

    const run = async () => {
      if (ranRef.current) return;
      ranRef.current = true;

      // 초기화: 라인
      await animate(".line-12", { pathLength: 0, opacity: 0 }, { duration: 0 });
      await animate(".line-23", { pathLength: 0, opacity: 0 }, { duration: 0 });
      await animate(".line-34", { pathLength: 0, opacity: 0 }, { duration: 0 });

      // 초기화: 카드
      for (const c of cards) {
        await animate(
          `.card-${c.id}`,
          {
            opacity: START_OPACITY,
            y: 12,
            scale: 0.985,
            filter: START_FILTER,
          },
          { duration: 0 },
        );
      }

      if (!alive) return;

      //  1) card1 등장
      await animate(
        ".card-1",
        { opacity: 1, y: 0, scale: 1, filter: END_FILTER },
        { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
      );

      if (!alive) return;

      //  2) 라인(1->2) 그려짐
      await animate(
        ".line-12",
        { pathLength: 1, opacity: 1 },
        { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
      );

      if (!alive) return;

      //  3) card2 등장
      await animate(
        ".card-2",
        { opacity: 1, y: 0, scale: 1, filter: END_FILTER },
        { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
      );

      if (!alive) return;

      //  4) card3 등장
      await animate(
        ".card-3",
        { opacity: 1, y: 0, scale: 1, filter: END_FILTER },
        { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
      );

      if (!alive) return;

      //  5) card4 등장
      await animate(
        ".card-4",
        { opacity: 1, y: 0, scale: 1, filter: END_FILTER },
        { duration: 0.5, ease: [0.22, 1, 0.36, 1] },
      );

      // await animate(".line-23", { pathLength: 1, opacity: 1 }, { duration: 0.55, ease: [0.22, 1, 0.36, 1] });
      // await animate(".line-34", { pathLength: 1, opacity: 1 }, { duration: 0.35, ease: [0.22, 1, 0.36, 1] });
    };

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) run();
      },
      { threshold: 0, rootMargin: "-50% 0px -50% 0px" },
    );

    io.observe(el);

    return () => {
      alive = false;
      io.disconnect();
    };
  }, [animate, cards]);

  return (
    <section className="w-full flex justify-center">
      <div
        ref={outerRef}
        className="w-full max-w-[623px] bg-[#F2F4F8] rounded-[24px] overflow-hidden"
        style={{
          height: scaledH,
          position: "relative",
          boxShadow: "0 1.813px 19.07px 0 rgba(48, 75, 109, 0.20)",
        }}
      >
        <img
          src="/assets/images/home/card_bg.png"
          alt=""
          draggable={false}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover", // 이미지가 꽉 차게
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0,
          }}
        />
        <div
          ref={(node) => {
            hostRef.current = node;
            (scope as any).current = node;
          }}
          style={{
            width: BASE_W,
            height: BASE_H,
            position: "absolute",
            left: 0,
            top: 0,
            transform: `scale(${scale})`,
            transformOrigin: "top left",
          }}
        >
          {/* 라인 */}
          <svg width={BASE_W} height={BASE_H} style={{ position: "absolute", inset: 0 }}>
            {/* line 1->2 */}
            <motion.path
              className="line-12"
              d={lineDefs.line12.d}
              fill="none"
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
            />
          </svg>

          {/* 카드들 */}
          {cards.map((c) => (
            <motion.img
              key={c.id}
              className={`card-${c.id}`}
              src={c.img}
              alt=""
              draggable={false}
              style={{
                position: "absolute",
                left: c.x,
                top: c.y,
                width: c.w,
                zIndex: c.z,
                userSelect: "none",

                //  처음부터 흐릿하게 "존재"
                opacity: START_OPACITY,
                transform: "translateY(12px) scale(0.985)",
                filter: START_FILTER,

                willChange: "transform, opacity, filter",
              }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
